import { useMutation } from "urql";
import { graphql } from "../../../../__generated__/gql";
import Modal from "../../../../ui/Modal/Modal";
import styles from "./DeleteArticlesModal.module.css";
import { useCallback, useState } from "react";
import { DeleteArticlesInput } from "../../../../__generated__/gql/graphql";
import Label from "../../../../ui/Label/Label";
import ConfirmationModal from "../../../../ui/Modal/ConfirmationModal";
import NumberInput from "../../../../ui/NumberInput/NumberInput";
import { useToast } from "../../../../ui/Toast/ToastContext";

const DeleteArticlesMutation = graphql(/* GraphQL */ `
  mutation deleteArticles($input: DeleteArticlesInput!) {
    deleteArticles(input: $input) {
      count
    }
  }
`);

function InnerDeleteArticlesModal({
  onClose,
  onDelete,
  numberOfArticles,
  filters,
  isOpen,
}: {
  onClose: () => void;
  onDelete?: () => void;
  numberOfArticles?: number;
  filters: DeleteArticlesInput["filters"];
  isOpen: boolean;
}) {
  const { showToast } = useToast();
  const [{ fetching }, deleteArticles] = useMutation(DeleteArticlesMutation);
  const [confirmationText, setConfirmationText] = useState("");

  const onSubmit = useCallback(async () => {
    await deleteArticles({
      input: {
        filters,
      },
    });

    if (numberOfArticles) {
      showToast({
        title: `${numberOfArticles} ${
          numberOfArticles === 1 ? "article" : "articles"
        } deleted`,
      });
    }
    onDelete?.();
    onClose();
  }, [deleteArticles, filters, numberOfArticles, onClose, onDelete, showToast]);

  return (
    <ConfirmationModal
      destructive
      loading={fetching || !isOpen}
      onClose={onClose}
      onSubmit={onSubmit}
      confirmDisabled={confirmationText !== numberOfArticles?.toString()}
      confirmationText="Delete"
    >
      <div>
        <Label label={`Type "${numberOfArticles}" to confirm deletion`}>
          <NumberInput
            className={styles.input}
            placeholder={numberOfArticles?.toString()}
            size="large"
            value={confirmationText}
            onChange={(e) => setConfirmationText(e.target.value)}
          />
        </Label>
      </div>
    </ConfirmationModal>
  );
}

function DeleteArticlesModal({
  isOpen,
  onClose,
  onDelete,
  numberOfArticles,
  filters,
}: {
  isOpen: boolean;
  onClose: () => void;
  onDelete?: () => void;
  numberOfArticles?: number;
  filters: DeleteArticlesInput["filters"];
}) {
  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={`Are you sure you want to delete ${numberOfArticles} ${
        numberOfArticles === 1 ? "article" : "articles"
      }?`}
    >
      <InnerDeleteArticlesModal
        onClose={onClose}
        onDelete={onDelete}
        numberOfArticles={numberOfArticles}
        isOpen={isOpen}
        filters={filters}
      />
    </Modal>
  );
}

export default DeleteArticlesModal;
